import React from "react";
import { motion, useReducedMotion, useScroll, useTransform } from "framer-motion";
import { ArrowRightIcon } from "lucide-react";
import { Link } from "react-router-dom";
import { useSite } from "../../context/SiteContext";
import { HERO_POSTER } from "../../data/homeContent";
import { AgaveMark } from "../culture/AgaveMark";
import { MarigoldPetals } from "../culture/MarigoldPetals";
const EASE_OUT = [0.23, 1, 0.32, 1] as const;
export function HeroReel() {
  const {
    t
  } = useSite();
  const reduceMotion = useReducedMotion();
  const {
    scrollY
  } = useScroll();
  const contentOpacity = useTransform(scrollY, [0, 420], [1, reduceMotion ? 1 : 0.15]);
  const contentY = useTransform(scrollY, [0, 420], [0, reduceMotion ? 0 : -48]);
  const mediaScale = useTransform(scrollY, [0, 600], [1, reduceMotion ? 1 : 1.08]);
  const reveal = (index: number) => reduceMotion ? {} : {
    initial: {
      opacity: 0,
      y: 14
    },
    animate: {
      opacity: 1,
      y: 0
    },
    transition: {
      duration: 0.3,
      ease: EASE_OUT,
      delay: 0.15 + index * 0.06
    }
  };
  return <section className="relative h-[calc(100svh-68px)] min-h-[560px] max-h-[940px] overflow-hidden bg-agavedeep text-cream" data-id="element-294" data-mp="162hserab61s4">
      <motion.div style={{
      scale: mediaScale
    }} className="absolute inset-0" data-id="element-295" data-mp="s15fkoqg2gag">
        <img src={HERO_POSTER} alt="" aria-hidden="true" decoding="async" className="ken-burns h-full w-full object-cover" data-id="element-296" data-mp="w38z80n9or6t" />
      </motion.div>
      <div className="absolute inset-0 bg-agavedeep/50" data-id="element-297" data-mp="n3dlpp68ok3m" />
      <MarigoldPetals data-id="element-298" data-mp="n56qt017ew9i" />

      <motion.div style={{
      opacity: contentOpacity,
      y: contentY
    }} className="relative mx-auto flex h-full max-w-7xl flex-col justify-end px-5 pb-10 sm:px-8 sm:pb-16" data-id="element-299" data-mp="8ehhk37azr8a">
        <motion.div {...reveal(0)} className="flex items-center gap-3" data-id="element-300" data-mp="3vixe6lz35yhm">
          <AgaveMark className="h-10 w-14 drop-shadow sm:h-12 sm:w-[72px]" data-id="element-301" data-mp="3vl0afnqu0nus" />
          <div data-id="element-302" data-mp="4c2xjfrwelff">
            <p className="font-serif text-3xl font-bold leading-none text-cream sm:text-4xl" data-id="element-303" data-mp="4bt93wmi4mbu">Blue Agave</p>
            <p className="mt-1 text-[11px] font-semibold uppercase tracking-[.22em] text-sunflower" data-id="element-304" data-mp="263v0o714o4yf">
              {t("Authentic Mexican Food · Victor, Idaho", "Auténtica comida mexicana · Victor, Idaho")}
            </p>
          </div>
        </motion.div>

        <motion.h1 {...reveal(1)} className="mt-6 max-w-3xl font-serif text-[44px] font-bold leading-[0.95] text-cream [text-shadow:0_2px_24px_rgba(0,0,0,.35)] sm:text-6xl lg:text-[84px]" data-id="element-305" data-mp="1sber0ndq18oj">
          {t("Authentic Mexican Food. Made to Celebrate.", "Comida mexicana auténtica. Hecha para celebrar.")}
        </motion.h1>

        <motion.p {...reveal(2)} className="mt-4 max-w-xl text-base leading-7 text-cream/90 sm:text-lg" data-id="element-306" data-mp="2g1snqr793vpg">
          {t("From our kitchen to your table — experience the flavor, color and spirit of Mexico.", "De nuestra cocina a tu mesa — vive el sabor, el color y el espíritu de México.")}
        </motion.p>

        <motion.div {...reveal(3)} className="mt-7 grid grid-cols-2 gap-3 sm:flex" data-id="element-307" data-mp="3ba4cflmx5b5u">
          <Link to="/locations" className="inline-flex min-h-12 items-center justify-center gap-2 whitespace-nowrap rounded-full bg-marigold px-6 text-sm font-bold text-agavedeep shadow-lg transition-[background-color,transform] duration-150 hover:bg-sunflower active:scale-[0.97] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cream sm:text-base" data-id="element-308" data-mp="3g7mk78p79kc9">
            {t("Visit Us", "Visítanos")}
            <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-309" data-mp="2reydjknjd0v" />
          </Link>
          <Link to="/catering" className="inline-flex min-h-12 items-center justify-center whitespace-nowrap rounded-full border border-cream/60 bg-agavedeep/40 px-6 text-sm font-bold text-cream backdrop-blur transition-[background-color,transform] duration-150 hover:bg-cream/15 active:scale-[0.97] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cream sm:text-base" data-id="element-310" data-mp="7suin9f09fi7">
            {t("Plan Your Event", "Planea tu evento")}
          </Link>
        </motion.div>
      </motion.div>
    </section>;
}